import { Document, Text, Page, View, Image } from '@react-pdf/renderer';
import useTranslate from 'hooks/useTranslate';
import { IMAGE_PATHS } from 'constants/imagePaths';
import React from 'react';
import { checkObjHasValues, generateRandomString } from 'utils';
import {
	Certification,
	Education,
	Experience,
	PersonalInfo,
	Projects,
	Skills,
	Social,
	Tools,
	Awards,
	Language,
	About,
	Hobbies,
} from './Sections';

interface Template2Props {
	resumeData: { [key: string]: any };
	styles: { [key: string]: any };
	isWebView?: boolean;
}

const Template2: React.FC<Template2Props> = ({ resumeData, styles, isWebView = false }) => {
	const {
		profilePicture,
		shortBio,
		experience,
		details,
		project,
		education,
		skills,
		tools,
		certification,
		social,
		manageTemplateFields,
		shortBioTitle,
		hobbiesTitle,
		langTitle,
		experienceTitle,
		projectTitle,
		educationTitle,
		toolTitle,
		certTitle,
		socialTitle,
		skillTitle,
		awards,
		hobbies,
		language,
		awardTitle,
	} = resumeData ?? {};

	const {
		details: SHOW_DETAILS,
		shortBio: SHOW_SHORT_BIO,
		experience: SHOW_EXPERIENCE,
		project: SHOW_PROJECT,
		education: SHOW_EDUCATION,
		skills: SHOW_SKILLS,
		tools: SHOW_TOOLS,
		social: SHOW_SOCIAL,
		certification: SHOW_CERTIFICATION,
		awards: SHOW_AWARDS,
		language: SHOW_LANGUAGE,
		hobbies: SHOW_HOBBIES,
	} = manageTemplateFields ?? {};

	const randomString = generateRandomString(8);
	const { translate } = useTranslate();

	return (
		<Document style={styles.document}>
			<Page size="A4" style={styles.page} wrap>
				<Image style={styles.bottomBG} src={IMAGE_PATHS.template2BottomBG} fixed />
				<View>
					<View style={styles.usernameBox}>
						<View style={styles.usernameBoxBG}></View>
						<Text style={styles.username}>{`${details?.firstName} ${details?.lastName}`}</Text>
					</View>
					{details?.designation && <Text style={styles.designation}>{details.designation}</Text>}
					<View style={styles.underlineBox}></View>
				</View>
				<View style={styles.aboutBox}>
					{(profilePicture || shortBio) && (
						<About
							data={{ profilePicture, shortBio }}
							styles={styles}
							headerTitle={shortBioTitle}
						/>
					)}
					<PersonalInfo data={details} styles={styles} />
				</View>
				<View style={styles.pageInfo}>
					<View style={styles.left}>
						{(SHOW_EDUCATION || isWebView) &&
							education?.length &&
							checkObjHasValues(education[0]) && (
								<Education
									data={education}
									styles={styles}
									headerTitle={educationTitle}
									randomString={randomString}
								/>
							)}
						{(SHOW_AWARDS || isWebView) && awards?.length && checkObjHasValues(awards[0]) && (
							<Awards data={awards} styles={styles} headerTitle={awardTitle} />
						)}
						{(SHOW_CERTIFICATION || isWebView) &&
							certification?.length &&
							checkObjHasValues(certification[0]) && (
								<Certification
									data={certification}
									styles={styles}
									headerTitle={certTitle}
									randomString={randomString}
								/>
							)}
						{(SHOW_LANGUAGE || isWebView) && language?.length && checkObjHasValues(language[0]) && (
							<Language data={language} styles={styles} headerTitle={langTitle} />
						)}
						{(SHOW_HOBBIES || isWebView) && hobbies?.length && checkObjHasValues(hobbies[0]) && (
							<Hobbies data={hobbies} styles={styles} headerTitle={hobbiesTitle} />
						)}
						{(SHOW_SOCIAL || isWebView) && social?.length && checkObjHasValues(social[0]) && (
							<Social
								data={social}
								styles={styles}
								headerTitle={socialTitle}
								randomString={randomString}
							/>
						)}
					</View>
					<View style={styles.right}>
						{(SHOW_EXPERIENCE || isWebView) &&
							experience?.length &&
							checkObjHasValues(experience[0]) && (
								<Experience
									data={experience}
									styles={styles}
									headerTitle={experienceTitle}
									translate={translate}
								/>
							)}
						{(SHOW_SKILLS || isWebView) && skills?.length && checkObjHasValues(skills[0]) && (
							<Skills data={skills} styles={styles} headerTitle={skillTitle} />
						)}
						{(SHOW_TOOLS || isWebView) && tools?.length && checkObjHasValues(tools[0]) && (
							<Tools data={tools} styles={styles} headerTitle={toolTitle} />
						)}
						{(SHOW_PROJECT || isWebView) && project?.length && checkObjHasValues(project[0]) && (
							<Projects data={project} styles={styles} headerTitle={projectTitle} />
						)}
					</View>
				</View>
			</Page>
		</Document>
	);
};

export default Template2;
